/**
 * phonicsArcadeData.js
 * Comprehensive Cambridge Young Learners (Pre-A1 Starters -> A1 Movers)
 * Phonics & Phonemic Awareness dataset for Arcade Phonics Games.
 */

export const PHONICS_ROUNDS = [
  // ── Short Vowels ──
  {
    id: 'short_a',
    sound: '/æ/ (short a)',
    ipa: 'æ',
    prompt: 'Tap all bubbles with the short /æ/ sound!',
    audioHint: 'Short a, as in cat and apple!',
    targets: [
      { word: 'cat', hasSound: true },
      { word: 'hat', hasSound: true },
      { word: 'apple', hasSound: true },
      { word: 'bag', hasSound: true },
      { word: 'map', hasSound: true },
      { word: 'bat', hasSound: true },
    ],
    distractors: [
      { word: 'car', hasSound: false },
      { word: 'cake', hasSound: false },
      { word: 'ball', hasSound: false },
      { word: 'star', hasSound: false },
      { word: 'rain', hasSound: false },
    ],
  },
  {
    id: 'short_e',
    sound: '/e/ (short e)',
    ipa: 'e',
    prompt: 'Tap all bubbles with the short /e/ sound!',
    audioHint: 'Short e, as in bed and red!',
    targets: [
      { word: 'bed', hasSound: true },
      { word: 'red', hasSound: true },
      { word: 'pen', hasSound: true },
      { word: 'hen', hasSound: true },
      { word: 'leg', hasSound: true },
      { word: 'ten', hasSound: true },
    ],
    distractors: [
      { word: 'bee', hasSound: false },
      { word: 'tree', hasSound: false },
      { word: 'sea', hasSound: false },
      { word: 'meat', hasSound: false },
      { word: 'key', hasSound: false },
    ],
  },
  {
    id: 'short_i',
    sound: '/ɪ/ (short i)',
    ipa: 'ɪ',
    prompt: 'Tap all bubbles with the short /ɪ/ sound!',
    audioHint: 'Short i, as in pig and fish!',
    targets: [
      { word: 'pig', hasSound: true },
      { word: 'sit', hasSound: true },
      { word: 'fish', hasSound: true },
      { word: 'milk', hasSound: true },
      { word: 'pink', hasSound: true },
      { word: 'ring', hasSound: true },
    ],
    distractors: [
      { word: 'ice', hasSound: false },
      { word: 'bike', hasSound: false },
      { word: 'kite', hasSound: false },
      { word: 'pie', hasSound: false },
      { word: 'night', hasSound: false },
    ],
  },
  {
    id: 'short_o',
    sound: '/ɒ/ (short o)',
    ipa: 'ɒ',
    prompt: 'Tap all bubbles with the short /ɒ/ sound!',
    audioHint: 'Short o, as in dog and hot!',
    targets: [
      { word: 'dog', hasSound: true },
      { word: 'hot', hasSound: true },
      { word: 'box', hasSound: true },
      { word: 'fox', hasSound: true },
      { word: 'frog', hasSound: true },
      { word: 'clock', hasSound: true },
    ],
    distractors: [
      { word: 'boat', hasSound: false },
      { word: 'nose', hasSound: false },
      { word: 'rose', hasSound: false },
      { word: 'snow', hasSound: false },
      { word: 'coat', hasSound: false },
    ],
  },
  {
    id: 'short_u',
    sound: '/ʌ/ (short u)',
    ipa: 'ʌ',
    prompt: 'Tap all bubbles with the short /ʌ/ sound!',
    audioHint: 'Short u, as in sun and bus!',
    targets: [
      { word: 'sun', hasSound: true },
      { word: 'bus', hasSound: true },
      { word: 'cup', hasSound: true },
      { word: 'duck', hasSound: true },
      { word: 'run', hasSound: true },
      { word: 'bug', hasSound: true },
    ],
    distractors: [
      { word: 'blue', hasSound: false },
      { word: 'moon', hasSound: false },
      { word: 'flute', hasSound: false },
      { word: 'tune', hasSound: false },
      { word: 'cube', hasSound: false },
    ],
  },

  // ── Long Vowels & Digraphs ──
  {
    id: 'long_a',
    sound: '/eɪ/ (long a - a_e / ai)',
    ipa: 'eɪ',
    prompt: 'Tap all bubbles with the long /eɪ/ sound!',
    audioHint: 'Long a, as in cake, rain, and train!',
    targets: [
      { word: 'cake', hasSound: true },
      { word: 'rain', hasSound: true },
      { word: 'train', hasSound: true },
      { word: 'snake', hasSound: true },
      { word: 'face', hasSound: true },
      { word: 'day', hasSound: true },
    ],
    distractors: [
      { word: 'cat', hasSound: false },
      { word: 'apple', hasSound: false },
      { word: 'hat', hasSound: false },
      { word: 'bag', hasSound: false },
      { word: 'star', hasSound: false },
    ],
  },
  {
    id: 'long_e',
    sound: '/iː/ (long e - ee / ea)',
    ipa: 'iː',
    prompt: 'Tap all bubbles with the long /iː/ sound!',
    audioHint: 'Long e, as in tree and green!',
    targets: [
      { word: 'tree', hasSound: true },
      { word: 'bee', hasSound: true },
      { word: 'green', hasSound: true },
      { word: 'sleep', hasSound: true },
      { word: 'leaf', hasSound: true },
      { word: 'feet', hasSound: true },
    ],
    distractors: [
      { word: 'bed', hasSound: false },
      { word: 'pen', hasSound: false },
      { word: 'red', hasSound: false },
      { word: 'leg', hasSound: false },
      { word: 'ten', hasSound: false },
    ],
  },
  {
    id: 'sh_sound',
    sound: '/ʃ/ (sh digraph)',
    ipa: 'ʃ',
    prompt: 'Tap all bubbles starting or ending with /ʃ/ (sh)!',
    audioHint: 'Shhh! Like ship, fish, and shoes!',
    targets: [
      { word: 'ship', hasSound: true },
      { word: 'fish', hasSound: true },
      { word: 'shop', hasSound: true },
      { word: 'shoe', hasSound: true },
      { word: 'dish', hasSound: true },
      { word: 'brush', hasSound: true },
    ],
    distractors: [
      { word: 'chip', hasSound: false },
      { word: 'chair', hasSound: false },
      { word: 'sun', hasSound: false },
      { word: 'cat', hasSound: false },
      { word: 'beach', hasSound: false },
    ],
  },
  {
    id: 'ch_sound',
    sound: '/tʃ/ (ch digraph)',
    ipa: 'tʃ',
    prompt: 'Tap all bubbles with the /tʃ/ (ch) sound!',
    audioHint: 'Ch-ch-ch! Like chair, beach, and chocolate!',
    targets: [
      { word: 'chair', hasSound: true },
      { word: 'chin', hasSound: true },
      { word: 'chick', hasSound: true },
      { word: 'beach', hasSound: true },
      { word: 'catch', hasSound: true },
      { word: 'cheese', hasSound: true },
    ],
    distractors: [
      { word: 'ship', hasSound: false },
      { word: 'shop', hasSound: false },
      { word: 'fish', hasSound: false },
      { word: 'tree', hasSound: false },
      { word: 'shoe', hasSound: false },
    ],
  },
  {
    id: 'rhyme_at',
    sound: '-at Rhyme Family',
    ipa: 'æt',
    prompt: 'Find all rhyming words ending in -at!',
    audioHint: 'Words that rhyme with cat and hat!',
    targets: [
      { word: 'cat', hasSound: true },
      { word: 'hat', hasSound: true },
      { word: 'bat', hasSound: true },
      { word: 'mat', hasSound: true },
      { word: 'rat', hasSound: true },
      { word: 'fat', hasSound: true },
    ],
    distractors: [
      { word: 'car', hasSound: false },
      { word: 'bed', hasSound: false },
      { word: 'dog', hasSound: false },
      { word: 'sun', hasSound: false },
      { word: 'pig', hasSound: false },
    ],
  },
  {
    id: 'rhyme_ing',
    sound: '-ing Rhyme Family',
    ipa: 'ɪŋ',
    prompt: 'Find all rhyming words ending in -ing!',
    audioHint: 'Words that rhyme with king and ring!',
    targets: [
      { word: 'ring', hasSound: true },
      { word: 'king', hasSound: true },
      { word: 'sing', hasSound: true },
      { word: 'wing', hasSound: true },
      { word: 'spring', hasSound: true },
    ],
    distractors: [
      { word: 'pink', hasSound: false },
      { word: 'ink', hasSound: false },
      { word: 'fish', hasSound: false },
      { word: 'milk', hasSound: false },
      { word: 'pig', hasSound: false },
    ],
  },
];
